<template>
    <div class="min-h-screen bg-gray-50">
        <div class="container mx-auto p-2 sm:p-6">
            <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                <div class="flex justify-between items-center p-3 border-b">
                    <h2 class="text-[13px] sm:text-[15px] font-semibold">VMD 제출 목록</h2>
                </div>
                <div class="divide-y divide-gray-100">
                    <!-- 로딩 상태 -->
                    <div v-if="isLoading" class="p-6 flex justify-center items-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <!-- VMD 제출 목록 -->
                    <template v-else>
                        <div v-for="(submission, index) in submissions" :key="`${submission.id}-${index}`"
                            class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer"
                            @click="openDetail(submission)">
                            <div class="p-3">
                                <div class="flex items-center justify-between">
                                    <div class="flex-1">
                                        <div class="flex items-center space-x-2">
                                            <span :class="getStatusClass(submission.status_code)"
                                                class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium">
                                                {{ getStatusText(submission.status_code) }}
                                            </span>
                                            <h3
                                                class="text-[13px] sm:text-[15px] font-medium group-hover:text-blue-600 truncate max-w-[200px] sm:max-w-none">
                                                {{ submission.title }}
                                            </h3>
                                        </div>
                                        <div
                                            class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                                            <span class="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                {{ formatDate(submission.date) }}
                                            </span>
                                            <!-- <span v-if="submission.comment" class="text-gray-600">
                                                {{ submission.comment }}
                                            </span> -->
                                        </div>
                                        <!-- 담당자 평가 코멘트가 있는 경우 표시 -->
                                        <div v-if="submission.report_result_comment"
                                            class="mt-2 relative bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-100">
                                            <!-- 말풍선 꼬리 -->
                                            <div
                                                class="absolute -top-2 left-5 w-3 h-3 bg-blue-50 border-t border-l border-blue-100 transform rotate-45">
                                            </div>
                                            <!-- 평가 내용 -->
                                            <div class="relative">
                                                <div class="flex items-center mb-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg"
                                                        class="h-4 w-4 text-blue-500 mr-1.5" fill="none"
                                                        viewBox="0 0 24 24" stroke="currentColor">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <span class="font-medium text-blue-700 text-[11px] sm:text-xs">담당자
                                                        평가</span>
                                                </div>
                                                <p
                                                    class="text-gray-600 text-[11px] sm:text-xs ml-5 truncate max-w-[200px] sm:max-w-[400px] md:max-w-[600px]">
                                                    {{ submission.report_result_comment }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- 데이터가 없는 경우 -->
                        <div v-if="submissions.length === 0" class="p-6 text-center text-gray-500">
                            제출한 VMD가 없습니다.
                        </div>
                    </template>
                </div>
                <div v-if="totalPages > 1" class="flex justify-center items-center p-3 border-t">
                    <v-pagination v-model="currentPage" :length="totalPages" :total-visible="getTotalVisible"
                        density="compact" color="grey-darken-1" class="pagination-text" size="small"
                        @update:model-value="handlePageChange"></v-pagination>
                </div>
            </div>
        </div>

        <!-- 상세 보기 사이드 패널 -->
        <transition name="fade">
            <div v-if="selectedSubmission" class="fixed inset-0 z-40 bg-gray-600 bg-opacity-75" @click="closeDetail">
            </div>
        </transition>
        <transition name="slide">
            <div v-if="selectedSubmission"
                class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
                <div class="p-4 sm:p-6">
                    <!-- 헤더 -->
                    <div class="flex justify-between items-center mb-4 sm:mb-6">
                        <h2 class="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">{{
                            selectedSubmission.title }}
                        </h2>
                        <button @click="closeDetail" class="text-gray-500 hover:text-gray-700">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- 메타 정보 -->
                    <div class="flex items-center space-x-4 mb-4 sm:mb-6">
                        <!-- <span class="flex items-center text-sm text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            연출공지 등록일: {{ formatDate(selectedSubmission.pub_date) }}
                        </span> -->
                        <span class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            제출일: {{ formatDate(selectedSubmission.reg_date) }}
                        </span>
                        <span v-if="selectedSubmission.mod_date"
                            class="flex items-center text-[11px] sm:text-xs text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            수정일: {{ formatDate(selectedSubmission.mod_date) }}
                        </span>
                        <span :class="getStatusClass(selectedSubmission.status_code)"
                            class="px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium">
                            {{ getStatusText(selectedSubmission.status_code) }}
                        </span>
                    </div>

                    <!-- 담당자 평가 -->
                    <div v-if="selectedSubmission.report_result_comment" class="mt-6">
                        <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">담당자 평가</h3>
                        <div class="bg-blue-50 p-4 rounded-lg border border-blue-100">
                            <p class="text-gray-600 text-[11px] sm:text-xs whitespace-pre-line">{{
                                selectedSubmission.report_result_comment }}
                            </p>
                        </div>
                    </div>

                    <!-- 코멘트 -->
                    <div v-if="isEditing" class="mt-6">
                        <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">코멘트 수정</h3>
                        <textarea v-model="editForm.comment" rows="4"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="코멘트를 입력해주세요"></textarea>
                    </div>
                    <div v-else-if="selectedSubmission.comment" class="mt-6">
                        <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">코멘트</h3>
                        <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <p class="text-gray-600 text-[11px] sm:text-xs whitespace-pre-line">{{
                                selectedSubmission.comment }}
                            </p>
                        </div>
                    </div>

                    <!-- 이미지 섹션 -->
                    <div class="space-y-6 mt-6">
                        <!-- 필수1 이미지 -->
                        <div>
                            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">필수1 이미지</h3>
                            <div v-if="isEditing" class="relative">
                                <div v-if="editForm.required1" class="relative aspect-video">
                                    <img :src="editForm.required1"
                                        class="w-full h-full object-contain rounded-lg border">
                                    <button @click="removeEditImage('required1')"
                                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                                        ×
                                    </button>
                                </div>
                                <div v-else
                                    class="aspect-video border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                                    @click="triggerEditImageUpload('required1')">
                                    <div class="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                            class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24"
                                            stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span class="text-[11px] sm:text-xs text-gray-500">이미지 업로드</span>
                                    </div>
                                </div>
                                <input type="file" ref="required1EditInput" class="hidden" accept="image/*"
                                    @change="handleEditImageUpload('required1', $event)">
                            </div>
                            <div v-else class="relative aspect-video">
                                <img v-if="selectedSubmission?.required1_url"
                                    :src="selectedSubmission.required1_url.url" loading="lazy"
                                    class="w-full h-full object-contain rounded-lg border"
                                    @click="openImageViewer(selectedSubmission.required1_url.url)">
                            </div>
                        </div>

                        <!-- 필수2 이미지 -->
                        <div>
                            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">필수2 이미지</h3>
                            <div v-if="isEditing" class="relative">
                                <div v-if="editForm.required2" class="relative aspect-video">
                                    <img :src="editForm.required2"
                                        class="w-full h-full object-contain rounded-lg border">
                                    <button @click="removeEditImage('required2')"
                                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                                        ×
                                    </button>
                                </div>
                                <div v-else
                                    class="aspect-video border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                                    @click="triggerEditImageUpload('required2')">
                                    <div class="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                            class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24"
                                            stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span class="text-[11px] sm:text-xs text-gray-500">이미지 업로드</span>
                                    </div>
                                </div>
                                <input type="file" ref="required2EditInput" class="hidden" accept="image/*"
                                    @change="handleEditImageUpload('required2', $event)">
                            </div>
                            <div v-else class="relative aspect-video">
                                <img v-if="selectedSubmission?.required2_url"
                                    :src="selectedSubmission.required2_url.url" loading="lazy"
                                    class="w-full h-full object-contain rounded-lg border"
                                    @click="openImageViewer(selectedSubmission.required2_url.url)">
                            </div>
                        </div>

                        <!-- 추가 이미지 -->
                        <div v-if="selectedSubmission?.additional_urls?.length > 0 || isEditing">
                            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">추가 이미지 (최대 5장)</h3>
                            <div v-if="isEditing" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div v-for="(url, index) in editForm.additional" :key="index"
                                    class="relative aspect-square">
                                    <img :src="url" class="w-full h-full object-cover rounded-lg border">
                                    <button @click="removeEditAdditionalImage(index)"
                                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                                        ×
                                    </button>
                                </div>
                                <div v-if="editForm.additional.length < 5"
                                    class="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                                    @click="triggerEditAdditionalImageUpload">
                                    <div class="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                            class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24"
                                            stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span class="text-[11px] sm:text-xs text-gray-500">이미지 추가</span>
                                    </div>
                                </div>
                                <input type="file" ref="additionalEditInput" class="hidden" accept="image/*" multiple
                                    @change="handleEditAdditionalImageUpload">
                            </div>
                            <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div v-for="(file, index) in selectedSubmission.additional_urls" :key="index"
                                    class="relative aspect-square">
                                    <img :src="file.url"
                                        class="w-full h-full object-cover rounded-lg border cursor-pointer"
                                        @click="openImageViewer(file.url)">
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 하단 버튼 영역 -->
                    <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-6">
                        <div class="flex flex-col sm:flex-row gap-3">
                            <!-- 연출공지 보기 버튼 추가 -->
                            <button @click="viewRequest"
                                class="flex-1 bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                연출공지 보기
                            </button>
                            <!-- 담당자 승인 상태가 아닐 때만 수정 버튼 표시 -->
                            <template v-if="selectedSubmission?.status_code !== 129">
                                <button v-if="!isEditing" @click="startEdit"
                                    class="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    수정하기
                                </button>
                                <template v-else>
                                    <button @click="handleUpdate"
                                        class="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center">
                                        저장
                                    </button>
                                    <button @click="cancelEdit"
                                        class="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center">
                                        취소
                                    </button>
                                </template>
                            </template>
                            <button v-if="!isEditing" @click="closeDetail"
                                class="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- 이미지 뷰어 -->
        <transition name="fade">
            <div v-if="selectedImage" class="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
                @click="closeImageViewer">
                <img :src="selectedImage" class="max-w-full max-h-full object-contain p-4">
                <button class="absolute top-4 right-4 text-white hover:text-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import vmdAPI from '@/api/vmd/vmd';
import Swal from 'sweetalert2';
import { resizeImage } from '@/utils/imageUtils';
import { useRouter } from 'vue-router';

// S3 기본 URL 환경변수
const S3_BASE_URL = process.env.VUE_APP_S3_URL;
const router = useRouter();

// S3 URL 구성 함수
const getS3Url = (filePath, uploadYear, isNew) => {
  if (!filePath) return null;
  if (isNew) {
    return `${S3_BASE_URL}/_static/images/vmd/${uploadYear}/${filePath}`;
  } else {
    return `${process.env.VUE_APP_BEFORE_API_URL}/images/reports/${filePath}`;
  }
};

// S3 URL로 객체 업데이트 함수
const updateFileUrl = (fileObj) => {
  if (!fileObj) return null;
  return {
    ...fileObj,
    url: getS3Url(fileObj.url, fileObj.upload_year, fileObj.is_new)
  };
};

// 캐시 추가
const submissionsCache = ref(new Map());
const isLoading = ref(false);
const submissions = ref([]);
const totalSubmissions = ref(0);
const currentPage = ref(1);
const itemsPerPage = 6;

const selectedSubmission = ref(null);
const selectedImage = ref(null);

// 수정 관련 상태 추가
const isEditing = ref(false);
const editForm = ref({
    comment: '',
    required1: null,
    required2: null,
    additional: [],
    year: new Date().getFullYear(),
    originalFiles: {
        required1: null,
        required2: null,
        additional: []
    }
});

const required1EditInput = ref(null);
const required2EditInput = ref(null);
const additionalEditInput = ref(null);

// 상태에 따른 클래스 반환
const getStatusClass = (status_code) => {
    switch (status_code) {
        case 127: // 제출완료
            return 'bg-blue-100 text-blue-800';
        case 129: // 담당자승인
            return 'bg-green-100 text-green-800';
        case 128: // 보완요청
            return 'bg-red-100 text-red-800';
        case 130: // 보완제출
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// 상태 코드에 따른 텍스트 반환 함수 추가
const getStatusText = (status_code) => {
    switch (status_code) {
        case 127:
            return '제출완료';
        case 129:
            return '담당자승인';
        case 128:
            return '보완요청';
        case 130:
            return '보완제출';
        default:
            return '알 수 없음';
    }
};

// VMD 제출 목록 조회 (캐시 적용)
const fetchSubmissions = async (page = currentPage.value) => {
    try {
        // 캐시된 데이터가 있으면 사용
        if (submissionsCache.value.has(page)) {
            const cachedData = submissionsCache.value.get(page);
            submissions.value = cachedData.submissions;
            totalSubmissions.value = cachedData.total;
            // 해시 처리
            handleRouteChange();
            return;
        }

        isLoading.value = true;
        const response = await vmdAPI.getVmdSubmissions(page, itemsPerPage);
        
        // S3 URL 구성
        const submissionsWithS3Url = response.data.submissions.map(submission => {
            if (submission.required1_url) {
                submission.required1_url = updateFileUrl(submission.required1_url);
            }
            if (submission.required2_url) {
                submission.required2_url = updateFileUrl(submission.required2_url);
            }
            if (submission.additional_urls && submission.additional_urls.length > 0) {
                submission.additional_urls = submission.additional_urls.map(file => 
                    updateFileUrl(file)
                );
            }
            return submission;
        });
        
        submissions.value = submissionsWithS3Url;
        totalSubmissions.value = response.data.total;

        // 캐시에 저장
        submissionsCache.value.set(page, {
            submissions: submissionsWithS3Url,
            total: response.data.total
        });

        // 캐시 크기 제한 (최대 5페이지)
        if (submissionsCache.value.size > 5) {
            const oldestKey = submissionsCache.value.keys().next().value;
            submissionsCache.value.delete(oldestKey);
        }

        // 해시 처리
        const hash = window.location.hash;
        if (hash && hash.startsWith('#submission-') && !window.history.state?.navigatedFromPagination) {
            const submissionId = hash.replace('#submission-', '');
            const submission = submissions.value.find(s => s.id === submissionId);
            
            if (submission) {
                openDetail(submission);
            } else {
                // 현재 페이지에서 찾지 못한 경우, 모든 페이지를 검색
                let found = false;
                let currentSearchPage = 1;
                
                while (!found && currentSearchPage <= response.data.totalPages) {
                    if (currentSearchPage !== page) { // 현재 페이지는 이미 검색했으므로 스킵
                        const searchResponse = await vmdAPI.getVmdSubmissions(currentSearchPage, itemsPerPage);
                        const submissionsWithS3Url = searchResponse.data.submissions.map(submission => {
                            if (submission.required1_url) {
                                submission.required1_url = updateFileUrl(submission.required1_url);
                            }
                            if (submission.required2_url) {
                                submission.required2_url = updateFileUrl(submission.required2_url);
                            }
                            if (submission.additional_urls && submission.additional_urls.length > 0) {
                                submission.additional_urls = submission.additional_urls.map(file => 
                                    updateFileUrl(file)
                                );
                            }
                            return submission;
                        });
                        
                        const foundSubmission = submissionsWithS3Url.find(s => s.id === submissionId);
                        
                        if (foundSubmission) {
                            // 해당 페이지로 이동하고 상세보기 열기
                            currentPage.value = currentSearchPage;
                            submissions.value = submissionsWithS3Url;
                            openDetail(foundSubmission);
                            found = true;
                            break;
                        }
                    }
                    currentSearchPage++;
                }
                
                if (!found) {
                    console.warn('제출 내용을 찾을 수 없습니다:', submissionId);
                }
            }
        }
    } catch (error) {
        console.error('VMD 제출 목록 조회 중 오류 발생:', error);
    } finally {
        isLoading.value = false;
    }
};

// 페이지네이션 계산
const totalPages = computed(() => Math.ceil(totalSubmissions.value / itemsPerPage));

// 페이지 변경 처리
const handlePageChange = (page) => {
    currentPage.value = page;
    fetchSubmissions(page);  // 페이지 변경 시 해당 페이지 데이터를 서버에서 가져옴
};

// 날짜 포맷
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);
};

// 페이지네이션 visible 계산
const getTotalVisible = computed(() => {
    const width = window.innerWidth;
    if (width >= 1024) return 7;
    if (width >= 768) return 5;
    return 3;
});

// editForm 초기화 함수 통합
const initEditForm = () => {
    editForm.value = {
        comment: '',
        required1: null,
        required2: null,
        additional: [],
        year: new Date().getFullYear(),
        originalFiles: {
            required1: null,
            required2: null,
            additional: []
        }
    };
};

// closeDetail 함수 수정
const closeDetail = () => {
    if (isEditing.value) {
        isEditing.value = false;
        initEditForm();
    }
    selectedSubmission.value = null;
    history.replaceState(null, '', window.location.pathname);
};

// cancelEdit 함수 수정
const cancelEdit = () => {
    isEditing.value = false;
    initEditForm();
};

// startEdit 함수 수정
const startEdit = () => {
    isEditing.value = true;
    editForm.value = {
        comment: selectedSubmission.value.comment || '',
        required1: selectedSubmission.value.required1_url?.url || null,
        required2: selectedSubmission.value.required2_url?.url || null,
        additional: selectedSubmission.value.additional_urls?.map(file => file.url) || [],
        year: new Date().getFullYear(),
        originalFiles: {
            required1: null,
            required2: null,
            additional: []
        }
    };
};

// 상세 보기 열기
const openDetail = async (submission) => {
    try {
        if (isEditing.value) {
            isEditing.value = false;
            initEditForm();
        }
        console.log('상세조회 요청 ID:', submission.id);
        const response = await vmdAPI.getVmdSubmissionDetail(submission.id);
        console.log('상세조회 응답 데이터:', response.data);
        
        // 이미지 정보 로깅
        console.log('이미지 정보 (변경 전):', {
            required1: response.data.required1_url,
            required2: response.data.required2_url,
            additional: response.data.additional_urls
        });
        
        // S3 URL 구성
        if (response.data.required1_url) {
            response.data.required1_url = updateFileUrl(response.data.required1_url);
        }
        if (response.data.required2_url) {
            response.data.required2_url = updateFileUrl(response.data.required2_url);
        }
        if (response.data.additional_urls && response.data.additional_urls.length > 0) {
            response.data.additional_urls = response.data.additional_urls.map(file => 
                updateFileUrl(file)
            );
        }

        // 이미지 정보 로깅 (변경 후)
        console.log('이미지 정보 (변경 후):', {
            required1: response.data.required1_url,
            required2: response.data.required2_url,
            additional: response.data.additional_urls
        });
        
        selectedSubmission.value = response.data;
        history.pushState({ type: 'submission', id: submission.id }, '', `#submission-${submission.id}`);
    } catch (error) {
        console.error('VMD 제출 상세 조회 중 오류 발생:', error);
    }
};

// 이미지 뷰어 열기
const openImageViewer = (url) => {
    selectedImage.value = url;
};

// 이미지 뷰어 닫기
const closeImageViewer = () => {
    selectedImage.value = null;
};

// 뒤로가기 처리
const handlePopState = () => {
    if (selectedImage.value) {
        closeImageViewer();
    } else if (selectedSubmission.value) {
        closeDetail();
    }
};

// 이미지 업로드 트리거
const triggerEditImageUpload = (type) => {
    if (type === 'required1') {
        required1EditInput.value.click();
    } else if (type === 'required2') {
        required2EditInput.value.click();
    }
};

const triggerEditAdditionalImageUpload = () => {
    additionalEditInput.value.click();
};

// 이미지 업로드 처리
const handleEditImageUpload = async (type, event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
        const resizedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            editForm.value[type] = e.target.result;
            editForm.value.originalFiles[type] = resizedFile;
        };
        reader.readAsDataURL(resizedFile);
    }
};

// 추가 이미지 업로드 처리
const handleEditAdditionalImageUpload = async (event) => {
    const files = event.target.files;
    const remainingSlots = 5 - editForm.value.additional.length;
    
    for (let i = 0; i < files.length && i < remainingSlots; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
            const resizedFile = await resizeImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                editForm.value.additional.push(e.target.result);
                editForm.value.originalFiles.additional.push(resizedFile);
            };
            reader.readAsDataURL(resizedFile);
        }
    }
    
    // 5장을 초과하는 경우 경고 메시지 표시
    if (files.length > remainingSlots) {
        Swal.fire({
            icon: 'warning',
            title: '알림',
            text: `추가 이미지는 최대 5장까지만 업로드할 수 있습니다. (현재 ${editForm.value.additional.length}장)`
        });
    }
};

// 이미지 제거
const removeEditImage = (type) => {
    editForm.value[type] = null;
    editForm.value.originalFiles[type] = null;
};

const removeEditAdditionalImage = (index) => {
    editForm.value.additional.splice(index, 1);
    editForm.value.originalFiles.additional.splice(index, 1);
};

// 수정 저장
const handleUpdate = async () => {
    try {
        // 필수 이미지 검증 (기존 이미지 또는 새로 업로드된 이미지 중 하나라도 있으면 됨)
        const hasRequired1 = editForm.value.required1 || editForm.value.originalFiles.required1;
        const hasRequired2 = editForm.value.required2 || editForm.value.originalFiles.required2;

        if (!hasRequired1 || !hasRequired2) {
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '필수 이미지 2장을 모두 업로드해주세요.'
            });
            return;
        }

        // 제출 확인
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: '수정하시겠습니까?',
            text: '수정된 내용으로 다시 제출됩니다.',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            reverseButtons: true
        });

        if (confirmed.isConfirmed) {
            // FormData 생성
            const formData = new FormData();
            let hasChanges = false;

            // 코멘트가 수정된 경우에만 전송
            if (editForm.value.comment !== selectedSubmission.value.comment) {
                formData.append('comment', editForm.value.comment);
                hasChanges = true;
            }

            // 연도 정보 추가
            formData.append('year', editForm.value.year);

            // 이미지 변경 확인 (삭제, 수정, 추가 모두 포함)
            const modifiedTypes = [];
            
            // 필수1 이미지 변경 확인
            if (editForm.value.required1 !== selectedSubmission.value.required1_url?.url) {
                modifiedTypes.push('0');
                if (editForm.value.originalFiles.required1) {
                    formData.append('pictures', editForm.value.originalFiles.required1);
                }
                hasChanges = true;
            }

            // 필수2 이미지 변경 확인
            if (editForm.value.required2 !== selectedSubmission.value.required2_url?.url) {
                modifiedTypes.push('1');
                if (editForm.value.originalFiles.required2) {
                    formData.append('pictures', editForm.value.originalFiles.required2);
                }
                hasChanges = true;
            }

            // 추가 이미지 변경 확인
            const originalAdditionalUrls = selectedSubmission.value.additional_urls?.map(file => file.url) || [];
            const currentAdditionalUrls = editForm.value.additional.filter(url => url.startsWith('http'));
            
            // 새로 추가된 이미지 처리
            if (editForm.value.originalFiles.additional.length > 0) {
                editForm.value.originalFiles.additional.forEach(file => {
                    formData.append('pictures', file);
                    modifiedTypes.push('2');
                });
                hasChanges = true;
            }

            // 삭제된 이미지 처리
            const deletedUrls = originalAdditionalUrls.filter(url => !currentAdditionalUrls.includes(url));
            console.log('삭제할 이미지 URL:', deletedUrls);
            deletedUrls.forEach(url => {
                const fileToDelete = selectedSubmission.value.additional_urls.find(file => file.url === url);
                console.log('삭제할 파일 정보:', fileToDelete);
                if (fileToDelete) {
                    modifiedTypes.push(`delete_${fileToDelete.id}`);
                }
            });
            console.log('수정된 타입:', modifiedTypes);
            if (deletedUrls.length > 0) {
                hasChanges = true;
            }

            if (hasChanges) {
                formData.append('modified_types', JSON.stringify(modifiedTypes));
                
                await vmdAPI.updateReport(selectedSubmission.value.report_id, formData);
                
                // 보완요청 상태였다면 보완제출로, 그 외에는 제출완료로 상태 변경
                const newStatusCode = selectedSubmission.value.status_code === 128
                    ? 130
                    : 127;
                
                await vmdAPI.updateStatus(selectedSubmission.value.id, newStatusCode);
                
                // 성공 메시지
                await Swal.fire({
                    icon: 'success',
                    title: '성공',
                    text: '수정이 완료되었습니다.'
                });

                // 수정 모드 종료
                isEditing.value = false;

                // 상세 정보 다시 불러오기
                const response = await vmdAPI.getVmdSubmissionDetail(selectedSubmission.value.id);
                
                // S3 URL 구성
                if (response.data.required1_url) {
                    response.data.required1_url = updateFileUrl(response.data.required1_url);
                }
                if (response.data.required2_url) {
                    response.data.required2_url = updateFileUrl(response.data.required2_url);
                }
                if (response.data.additional_urls && response.data.additional_urls.length > 0) {
                    response.data.additional_urls = response.data.additional_urls.map(file => 
                        updateFileUrl(file)
                    );
                }
                
                selectedSubmission.value = response.data;
                
                // 목록의 코멘트와 상태도 업데이트
                const submissionIndex = submissions.value.findIndex(s => s.id === selectedSubmission.value.id);
                if (submissionIndex !== -1) {
                    submissions.value[submissionIndex] = {
                        ...submissions.value[submissionIndex],
                        comment: response.data.comment,
                        status_code: newStatusCode,
                        status_name: getStatusText(newStatusCode)
                    };
                }
            } else {
                Swal.fire({
                    icon: 'info',
                    title: '알림',
                    text: '변경된 내용이 없습니다.'
                });
            }
        }
    } catch (error) {
        console.error('VMD 제출 수정 중 오류 발생:', error);
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: error.response?.data?.error || '수정 중 오류가 발생했습니다.'
        });
    }
};

// 라우터 변경 감지 함수 수정
const handleRouteChange = () => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#submission-')) {
        const submissionId = hash.replace('#submission-', '');
        const submission = submissions.value.find(s => s.id === submissionId);
        if (submission) {
            openDetail(submission);
        }
    }
};

// 이벤트 리스너 등록/해제
onMounted(() => {
    fetchSubmissions();
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', () => {
        // 해시 변경 시 목록을 다시 불러와서 최신 데이터로 처리
        fetchSubmissions(currentPage.value);
    });
});

onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('hashchange', handleRouteChange);
});

// 연출공지 보기 함수 추가
const viewRequest = () => {
    if (selectedSubmission.value) {
        const requestId = selectedSubmission.value.id;
        // 현재 상세보기 닫기
        selectedSubmission.value = null;
        // VMD 요청사항 페이지로 이동
        router.push({
            name: 'VmdRequests',
            hash: `#request-${requestId}`
        });
    }
};
</script>

<style scoped>
/* 말풍선 애니메이션 */
.bg-blue-50 {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 새로운 스타일 */
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (min-width: 640px) {
    :deep(.pagination-text) {
        font-size: 12px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 12px;
    }
}

@media (min-width: 1024px) {
    :deep(.pagination-text) {
        font-size: 14px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 14px;
    }
}

@media (min-width: 1280px) {
    :deep(.pagination-text) {
        font-size: 15px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 15px;
    }
}
</style>
